import { browseFindingInvestigation } from "./alertTriageClient.js";
import { findingField } from "./discoveryQueries.js";
import { parseTs } from "./alertsQueue.js";
import { runDiscoveryRead } from "./discoveryReadQueue.js";

export const TIMELINE_DAY = 86400000;
export const TIMELINE_PAGE_SIZE = 50;
const changed = () => new Error("The investigation changed. Reload the timeline to try again.");
const pred = (field, value, op = "EQ") => ({ pred: { field: findingField(field), op: `OPERATOR_${op}`, value } });
const and = (...children) => ({ andExpr: { children: children.filter(Boolean) } });
const dim = (field, outputAlias) => ({ field: findingField(field), outputAlias });
const metric = (field, op, outputAlias, predicate) => ({ outputAlias, computed: { field: findingField(field), op: `AGGREGATE_OP_${op}`, ...(predicate ? { predicate } : {}) } });

export function timelineWindow(grouping, at, now = Date.now()) {
    const anchor = parseTs(at);
    if (anchor === null) throw new Error("This finding has no review time.");
    const end = grouping === "endpoint" ? anchor + TIMELINE_DAY / 2 : Math.min(now, anchor + 45 * TIMELINE_DAY) + 1;
    return { start: end - (grouping === "endpoint" ? TIMELINE_DAY : 90 * TIMELINE_DAY), end };
}
export function timelineRange(window) {
    if (!Number.isFinite(window?.start) || !Number.isFinite(window?.end) || window.end <= window.start || window.end - window.start > 90 * TIMELINE_DAY) throw new Error("Choose a timeline window of at most 90 days.");
    return { absolute: { start: new Date(window.start).toISOString(), end: new Date(window.end).toISOString() } };
}
function baseSpec(grouping, subject, window) {
    if (!["session", "endpoint"].includes(grouping) || !subject?.id || subject.unattributed) throw new Error("Choose an attributed endpoint or Prompt Session for the timeline.");
    const filter = pred(grouping === "session" ? "TRACE_ID" : "ENDPOINT_ID", subject.id);
    return { root: { alias: "f", entity: "ENTITY_TYPE_CONVERSATION_REVIEW_FINDING", stepFilter: [filter] }, timeRange: timelineRange(window), filter };
}
function membership(members) {
    if (!members.length || members.length > 5000 || members.some((m) => !m.finding_id)) throw changed();
    const ids = [...new Set(members.map((m) => m.finding_id))];
    return { orExpr: { children: Array.from({ length: Math.ceil(ids.length / 1000) }, (_, i) => pred("ID", ids.slice(i * 1000, (i + 1) * 1000), "IN")) } };
}
export function timelineExtentSpec(grouping, subject, window) {
    return { ...baseSpec(grouping, subject, window), output: { metrics: [metric("COMPLETED_AT", "MIN", "first_at"), metric("COMPLETED_AT", "MAX", "last_at"), metric("ID", "COUNT_DISTINCT", "count")] }, limit: 1 };
}
export function timelineDomain(grouping, window, rows) {
    if (grouping === "endpoint") return window;
    const count = Number(rows[0]?.count);
    if (rows.length !== 1 || !Number.isSafeInteger(count) || count < 0) throw new Error("The timeline returned invalid finding counts.");
    if (!count) return window;
    const start = parseTs(rows[0]?.first_at), last = parseTs(rows[0]?.last_at);
    if (start === null || last === null || start < window.start || last >= window.end || last < start) throw new Error("The timeline returned invalid review times.");
    return { start: Math.max(window.start, start - 60000), end: Math.min(window.end, last + 60000) };
}
export function timelineBucketSeconds(domain) {
    return Math.max(60, Math.ceil((domain.end - domain.start) / 1000 / 48 / 60) * 60);
}
export function timelineBucketsSpec(grouping, subject, domain, members) {
    return { ...baseSpec(grouping, subject, domain), output: {
        dimensions: [{ ...dim("COMPLETED_AT", "bucket"), bucketSeconds: timelineBucketSeconds(domain) }],
        metrics: [metric("ID", "COUNT_DISTINCT", "total"), metric("ID", "COUNT_DISTINCT", "collected", membership(members))],
    }, orderBy: [{ outputAlias: "bucket", direction: "DIRECTION_ASC" }], limit: 50 };
}
export function timelineBuckets(rows, domain) {
    const width = timelineBucketSeconds(domain) * 1000;
    const start = Math.floor(domain.start / width) * width;
    const result = Array.from({ length: Math.ceil((domain.end - start) / width) }, (_, i) => ({ start: Math.max(domain.start, start + i * width), end: Math.min(domain.end, start + (i + 1) * width), total: 0, collected: 0, other: 0 }));
    const seen = new Set();
    for (const row of rows) {
        const at = parseTs(row.bucket), index = (at - start) / width;
        const total = Number(row.total), collected = Number(row.collected);
        if (at === null || !Number.isInteger(index) || !result[index] || seen.has(index) || !Number.isSafeInteger(total) || !Number.isSafeInteger(collected) || collected < 0 || total < collected) throw new Error("The timeline counts changed. Reload to try again.");
        seen.add(index); Object.assign(result[index], { total, collected, other: total - collected });
    }
    return result;
}
export function timelineMembersSpec(grouping, subject, window, members, lane, cursor = null, sensitive = false) {
    if (!["collected", "other"].includes(lane)) throw new Error("Choose a timeline row.");
    const spec = baseSpec(grouping, subject, window);
    const saved = membership(members);
    spec.filter = and(spec.filter, lane === "collected" ? saved : { notExpr: { child: saved } }, cursor && { orExpr: { children: [pred("COMPLETED_AT", cursor.completed_at, "GT"), and(pred("COMPLETED_AT", cursor.completed_at), pred("ID", cursor.finding_id, "GT"))] } });
    return { ...spec, output: { dimensions: [dim("ID", "finding_id"), dim("TRACE_ID", "trace_id"), dim("REPORT_ID", "report_id"), dim("ENDPOINT_ID", "endpoint_id"), dim("COMPLETED_AT", "completed_at"), dim("BEHAVIOUR_SEVERITY", "behaviour_severity"), ...(sensitive ? [dim("TITLE", "title")] : [])] },
        orderBy: [{ outputAlias: "completed_at", direction: "DIRECTION_ASC" }, { outputAlias: "finding_id", direction: "DIRECTION_ASC" }], limit: TIMELINE_PAGE_SIZE + 1 };
}
export function validateTimelineMembers(rows, grouping, subject, window, members, lane) {
    const saved = new Map(members.map((m) => [m.finding_id, m]));
    const seen = new Set();
    if (rows.length > TIMELINE_PAGE_SIZE + 1) throw changed();
    for (const row of rows) {
        const at = parseTs(row.completed_at), member = saved.get(row.finding_id);
        if (!row.finding_id || !row.trace_id || !row.report_id || seen.has(row.finding_id) || at === null || at < window.start || at >= window.end
            || row[grouping === "session" ? "trace_id" : "endpoint_id"] !== subject.id || Boolean(member) !== (lane === "collected")
            || (member && (member.report_id !== row.report_id || member.trace_id !== row.trace_id))) throw changed();
        seen.add(row.finding_id);
    }
    return rows.slice(0, TIMELINE_PAGE_SIZE).map((row) => ({ ...row, completed_at: typeof row.completed_at === "string" ? row.completed_at : new Date(parseTs(row.completed_at)).toISOString() }));
}

export async function loadTimelineMembership({ incidentId, version, grouping, subject, signal, browse = browseFindingInvestigation }) {
    const members = [], ids = new Set(), tokens = new Set();
    let token = "";
    for (let page = 0; page < 100; page++) {
        signal?.throwIfAborted();
        const data = await runDiscoveryRead(() => browse(incidentId, { grouping, subjectId: subject.id, memberPageToken: token, signal }), { signal });
        signal?.throwIfAborted();
        if (data.version !== version || data.selected?.id !== subject.id || data.selected?.findingCount !== subject.findingCount || data.findings.length > 50) throw changed();
        for (const m of data.findings) {
            if (!m.finding_id || !m.trace_id || !m.report_id || parseTs(m.completed_at) === null || ids.has(m.finding_id) || m[grouping === "session" ? "trace_id" : "endpoint_id"] !== subject.id) throw changed();
            ids.add(m.finding_id); members.push(m);
        }
        if (members.length > 5000) throw changed();
        token = data.nextMemberPageToken;
        if (!token) { if (members.length !== subject.findingCount) throw changed(); return members; }
        if (tokens.has(token)) throw changed();
        tokens.add(token);
    }
    throw changed();
}
